import { useEffect, useState, useRef } from 'react';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { Button, Form, Col, Space } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import StyledModal from 'common/components/modal';
import { ThumbnailItem } from 'common/components';
import {
  ImageError,
  ImageUpload,
} from 'common/components/product-add/components/AdditionalImagerySection';
import { useUploadProductImage } from 'common/components/product-add/components/hooks/useUploadProductImage';
import { CustomNotification } from 'common/components';
import {
  FRONT_PLANOGRAM_IMAGE_ID,
  PLANOGRAM_IMAGE_TYPES,
} from 'common/components/product-add/components/constants';
import {
  checkIsTiffFile,
  convertTiffToPreview,
  getErrorMessageForImageType,
} from 'common/components/product-add/components/utils/images';
import tiff from 'assets/file-formats/tiff.png';

import gridSelector from 'common/components/grid-view/controllers/selectors';

import { lowercaseFirstChar } from 'hooks/useValidateSelfLoadedProduct';

import { useGetMemberId, useGetProductFullView } from 'hooks';

import './RequiredProductImagery.less';

export const RequiredProductImagery = ({
  fieldFullPath,
  displayName,
  primaryErrors,
  productImagery,
  entityId,
  mode,
  validationTimestamp,
}) => {
  const [visibleImagery, setVisibleImagery] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [imageData, setImageData] = useState(null);
  const [shouldHideError, setShouldHideError] = useState(false);

  const shouldHideErrorRef = useRef(shouldHideError);
  const lastTimestampRef = useRef(null);

  const { productFull } = useGetProductFullView({
    productId: entityId,
    enabled: Boolean(entityId) && mode === 'edit',
  });

  const selectedProductsGrid = useSelector(
    gridSelector.makeSelectDetailCurrentITemsSelection()
  );

  const selectedProductGrid =
    selectedProductsGrid?.find(
      (item) => parseInt(item?.id) === parseInt(entityId)
    ) ?? {};

  const { memberId: _memberId } = useGetMemberId();

  const memberId =
    mode === 'add'
      ? selectedProductGrid?.ownerId
      : mode === 'edit'
        ? productFull?.ownerId
        : _memberId;

  // Keep ref in sync with state
  useEffect(() => {
    shouldHideErrorRef.current = shouldHideError;
  }, [shouldHideError]);

  // Reset when validation timestamp changes
  useEffect(() => {
    if (
      validationTimestamp &&
      validationTimestamp !== lastTimestampRef.current &&
      shouldHideErrorRef.current
    ) {
      lastTimestampRef.current = validationTimestamp;
      setShouldHideError(false);
    }
  }, [validationTimestamp]);

  const transformFieldFullPath = lowercaseFirstChar(fieldFullPath);

  const formInstance = Form.useFormInstance();

  // Get the current error object from primaryErrors
  const currentErrorObj = primaryErrors.find(
    (err) => err?.fieldName?.toLowerCase() === fieldFullPath?.toLowerCase()
  );

  // Compute displayError directly
  const displayError = shouldHideError
    ? ''
    : currentErrorObj?.errorMessage || '';
  const isWarning = currentErrorObj?.isWarning || false;

  // Reset shouldHideError when validation completes (primaryErrors changes)
  // Using ref to avoid adding shouldHideError to deps
  useEffect(() => {
    if (shouldHideErrorRef.current) {
      setShouldHideError(false);
    }
  }, [primaryErrors]);

  // Single useEffect for initialization
  useEffect(() => {
    if (!isEmpty(productImagery)) {
      const initialProductImage = productImagery[transformFieldFullPath];
      setImageData(initialProductImage);
      formInstance.setFieldsValue({
        [transformFieldFullPath]: initialProductImage?.imageId,
      });
    }
  }, [fieldFullPath, formInstance, productImagery, transformFieldFullPath]);

  const uploadImageryMutation = useUploadProductImage({
    detectBarcode: false,
    memberId,
  });

  const handleFileChange = (fileList) => {
    const latestFile = fileList[fileList.length - 1];

    if (latestFile?.status?.includes('error')) return;

    if (latestFile?.originFileObj) {
      setPendingFile(latestFile.originFileObj);
    }
  };

  const handleUploadImagery = async () => {
    if (!pendingFile) return;

    uploadImageryMutation.mutate(pendingFile, {
      onSuccess: async (response) => {
        if (response?.isSuccess && response?.data) {
          const isTiff = checkIsTiffFile(pendingFile);
          const imageId = response?.data?.imageId;

          // Hide error immediately after successful upload
          setShouldHideError(true);

          const updateImageData = (previewUrl) => {
            const newImageData = {
              imageId,
              previewUrl,
              isTiff,
              status: 'draft',
            };

            setImageData(newImageData);
            formInstance.setFieldsValue({
              [transformFieldFullPath]: imageId,
            });

            // Trigger validation - when primaryErrors updates, useEffect will reset shouldHideError
            formInstance
              .validateFields([transformFieldFullPath])
              .catch(() => {});
          };

          if (isTiff) {
            try {
              const previewUrl = await convertTiffToPreview(pendingFile);
              updateImageData(previewUrl);
            } catch (error) {
              updateImageData(tiff);
              CustomNotification.warning(
                'TIFF preview could not be generated, showing placeholder icon'
              );
            }
          } else {
            const reader = new FileReader();
            reader.onload = (e) => {
              updateImageData(e.target.result);
            };
            reader.readAsDataURL(pendingFile);
          }

          setVisibleImagery(false);
          setPendingFile(null);
          CustomNotification.success('Uploaded successfully');
        } else {
          CustomNotification.error(
            response?.message ?? 'Failed to upload front planogram'
          );
        }
      },
      onError: (error) => {
        CustomNotification.error(
          error?.message ?? 'An error occurred while uploading'
        );
      },
    });
  };

  const handleRemoveImage = () => {
    setImageData(null);
    formInstance.setFieldsValue({
      [transformFieldFullPath]: null,
    });
    setPendingFile(null);
    setShouldHideError(false);

    // Trigger validation to update errors after removing image
    formInstance.validateFields([transformFieldFullPath]).catch(() => {});
  };

  const handleCancelUpload = () => {
    setPendingFile(null);
    uploadImageryMutation.reset();
    setVisibleImagery(false);
  };

  return (
    <div>
      <Form.Item name={transformFieldFullPath}>
        <Col flex='auto'>
          {imageData && (
            <div
              style={{
                position: 'relative',
                display: 'inline-block',
                paddingRight: 6,
              }}
            >
              <ThumbnailItem
                hideCheckbox
                style={{ marginLeft: 0 }}
                imageThumbnail={imageData?.previewUrl}
                labelThumbnail={displayName}
              />
              <div className='front-planogram-wrapper'>
                <span className='front-planogram-close'>
                  <CloseCircleOutlined
                    style={{
                      fontSize: 20,
                      color: '#ff4d4f',
                      cursor: 'pointer',
                      backgroundColor: '#fff',
                      borderRadius: '50%',
                      display: 'block',
                      pointerEvents: 'auto',
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveImage();
                    }}
                  />
                </span>
              </div>
            </div>
          )}
          <Space style={{ width: '100%' }}>
            <Button
              onClick={() => setVisibleImagery(true)}
              loading={uploadImageryMutation.isLoading}
            >
              {imageData ? 'Update Image' : 'Upload Image'}
            </Button>
            {displayError && (
              <ImageError
                errorMessage={displayError}
                isWarning={isWarning}
                style={{ padding: '4px 8px' }}
                className='required-product__imagery-error'
              />
            )}
          </Space>
        </Col>
      </Form.Item>

      {visibleImagery && (
        <StyledModal
          visible={visibleImagery}
          maskClosable={false}
          onCancel={handleCancelUpload}
          onOk={handleUploadImagery}
          closable={!uploadImageryMutation.isLoading}
          title={imageData ? 'Update Image' : 'Upload Image'}
          okText='Upload'
          cancelText='Cancel'
          okButtonProps={{
            disabled: !pendingFile,
            loading: uploadImageryMutation.isLoading,
          }}
        >
          <ImageUpload
            onFileChange={handleFileChange}
            supportTypes={PLANOGRAM_IMAGE_TYPES}
            customErrorMessage={getErrorMessageForImageType(
              FRONT_PLANOGRAM_IMAGE_ID
            )}
          />
        </StyledModal>
      )}
    </div>
  );
};

export const ModalUploadImagery = ({ onFileChange, ...props }) => {
  return (
    <StyledModal
      maskClosable={false}
      title='Upload Image'
      okText='Upload'
      cancelText='Cancel'
      {...props}
    >
      <ImageUpload onFileChange={onFileChange} />
    </StyledModal>
  );
};
